import { Flex, IconFont, Input, Popover } from '@byecode/ui'
import type { BackgroundNoImageProtocol, Border } from '@lighthouse/core'
import { BACKGROUND_TYPE, EDGE_MODE } from '@lighthouse/core'
import { FilledInput, FillPickerPopoverDropdown, ListItem4ByecodeUi } from '@lighthouse/shared'
import isDeepEqual from 'fast-deep-equal'
import produce from 'immer'
import React from 'react'

import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'
import { transform2Integer } from '@/utils/number'

interface BorderFieldProps {
    label?: React.ReactNode
    value: Border | undefined
    onChange: (val: Border) => void

    customViewDsId?: string
}

export const BorderField: React.FC<BorderFieldProps> = ({ label, value, onChange, customViewDsId }) => {
    const { each, mode, all, color } = value || {}
    const colorSystemMethods = useColorSystemAction()

    const variableTypeOption = useGetColorVariableOptions({
        customViewDsId
    })

    return (
        <>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                {label}
                <Popover width={300} position="left-start" withinPortal autoUpdate={false} offsetOptions={168}>
                    <Popover.Target>
                        <FilledInput
                            value={value}
                            onClear={() => {
                                onChange({ type: BACKGROUND_TYPE.color })
                            }}
                            style={{ width: 180 }}
                            // variableTypeOption={variableTypeOption}
                        />
                    </Popover.Target>
                    <FillPickerPopoverDropdown
                        title="填充"
                        value={value}
                        onChange={v => {
                            const val = {
                                ...(v as BackgroundNoImageProtocol),
                                mode: mode || EDGE_MODE.each,
                                each: each || [1, 1, 1, 1],
                                all: all || 1
                            }
                            if (isDeepEqual(val, value)) {
                                return
                            }
                            onChange(val)
                        }}
                        enabledBackgroundTypes={[BACKGROUND_TYPE.color /* , BACKGROUND_TYPE.variable */]}
                        // variableTypeOption={variableTypeOption}
                        {...colorSystemMethods}
                    />
                </Popover>
            </ListItem4ByecodeUi>
            {(color || value?.variable) && (
                <>
                    <ListItem4ByecodeUi justifyContent="flex-end">
                        <Flex styles={{ root: { width: 180 } }} gap="8px">
                            <Input
                                value={each?.[0]}
                                onChange={e => {
                                    if (!value) {
                                        return
                                    }
                                    const val = transform2Integer(e.target.value)
                                    const newValue = produce(value, draft => {
                                        if (!draft || !draft.each) {
                                            return
                                        }
                                        draft.each[0] = val
                                    })
                                    onChange(newValue)
                                }}
                                prefix={<IconFont type="LeftStroke" />}
                                placeholder=""
                                onFocus={e => {
                                    e.target.select()
                                }}
                            />
                            <Input
                                value={each?.[1]}
                                onChange={e => {
                                    if (!value) {
                                        return
                                    }
                                    const val = transform2Integer(e.target.value)
                                    const newValue = produce(value, draft => {
                                        if (!draft || !draft.each) {
                                            return
                                        }
                                        draft.each[1] = val
                                    })
                                    onChange(newValue)
                                }}
                                prefix={<IconFont type="TopStroke" />}
                                placeholder=""
                                onFocus={e => {
                                    e.target.select()
                                }}
                            />
                        </Flex>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="flex-end">
                        <Flex styles={{ root: { width: 180 } }} gap="8px">
                            <Input
                                value={each?.[2]}
                                onChange={e => {
                                    if (!value) {
                                        return
                                    }
                                    const val = transform2Integer(e.target.value)
                                    const newValue = produce(value, draft => {
                                        if (!draft || !draft.each) {
                                            return
                                        }
                                        draft.each[2] = val
                                    })
                                    onChange(newValue)
                                }}
                                prefix={<IconFont type="RightStroke" />}
                                placeholder=""
                                onFocus={e => {
                                    e.target.select()
                                }}
                            />
                            <Input
                                value={each?.[3]}
                                onChange={e => {
                                    if (!value) {
                                        return
                                    }
                                    const val = transform2Integer(e.target.value)
                                    const newValue = produce(value, draft => {
                                        if (!draft || !draft.each) {
                                            return
                                        }
                                        draft.each[3] = val
                                    })
                                    onChange(newValue)
                                }}
                                prefix={<IconFont type="BottomStroke" />}
                                placeholder=""
                                onFocus={e => {
                                    e.target.select()
                                }}
                            />
                        </Flex>
                    </ListItem4ByecodeUi>
                </>
            )}
        </>
    )
}
